import {format} from 'date-fns';
import {v4 as uuidv4} from 'uuid';
import {getEnv} from '../../common/env/env';
import {getSingleBadge} from '../../common/getSingleBadge';
import {t} from '../../common/getTranslations';
import {ItemSeriesDto} from '../../common/shared/api/gateway/ItemSeriesDto.interface';
import {ItemSingleDto} from '../../common/shared/api/gateway/ItemSingleDto.interface';
import {PurchaseTypeVo} from '../../common/shared/api/nicolas/vo/enums/PurchaseTypeVo.enum';
import {SlideTypeVo} from '../../common/shared/api/nicolas/vo/enums/SlideTypeVo.enum';
import {PurchaseInfoDto} from '../../common/shared/api/nicolas/vo/PurchaseInfoDto.interface';
import {TicketTypeVo} from '../../common/shared/api/nicolas/vo/TicketTypeVo.enum';
import {getDateDifference} from '../../common/shared/utils/getDateDifference';
import {getDiscountRateText} from '../../common/shared/utils/getDiscountRateText';
import {getKageUrl} from '../../common/shared/utils/getKageUrl';
import {getRemainText} from '../../common/shared/utils/getRemainText';
import {parseDate} from '../../common/shared/utils/parseDate';
import {isPreviewBm} from '../../domain/content/[seriesId]/util/isPreviewBm';
import {Bm, EventLog, ItemType, SingleListViewItem} from '../generated/types';
import {createBm} from './Bm';
import {createSingle} from './Single';

export function createSingleListViewItem(param: {
  contentHomeItemSingleDto: ItemSingleDto;
  itemSeriesDto: ItemSeriesDto;
}): SingleListViewItem {
  if ('contentHomeItemSingleDto' in param) {
    return withContentHomeItemSingleDto({
      itemSingleDto: param.contentHomeItemSingleDto,
      itemSeriesDto: param.itemSeriesDto,
    });
  } else {
    throw new Error('지원하지 않는 타입입니다');
  }
}

function withContentHomeItemSingleDto({
  itemSingleDto,
  itemSeriesDto,
}: {
  itemSingleDto: ItemSingleDto;
  itemSeriesDto: ItemSeriesDto;
}): SingleListViewItem {
  const {
    title,
    thumbnail,
    product_id,
    slide_type,
    start_sale_dt,
    is_free,
    hidden,
    free_change_dt,
    page_count,
    waitfree_blocked,
    badge,
    operator_property,
  } = itemSingleDto;
  const {purchase_info, last_view} = itemSingleDto.service_property || {};
  const bm = createBm({
    businessModel: itemSeriesDto.business_model!,
    isAllFree: itemSeriesDto.is_all_free,
    isWaitfree: itemSeriesDto.is_waitfree,
  });
  const currentDate = new Date();
  const ticket_info = operator_property?.ticket_info;
  const purchaseInfo = getPurchaseInfo({
    purchase_info,
    currentDate,
  });
  const showPriceList = ticket_info && !is_free && !purchaseInfo.canRead;
  const badgeList = getSingleBadge({
    badge,
    is_free,
    hidden,
    bm,
    waitfreeBlocked: waitfree_blocked,
    last_view,
    purchaseInfo: purchase_info,
    canRead: purchaseInfo.canRead,
  });

  const priceList: string[] = [];

  if (showPriceList) {
    /**
     * 캐시타입 단행본 회차 리스트에 가격 표기는 서버에서 정렬해서 내려주지 않음.
     * 대여(RTXX) -> 소장(TTXX) 순으로 표기해야하므로 알파벳 내림차순 정렬로
     * 소장(T)보다 대여(R)가 먼저 노출되도록 sorting
     */
    const sortedTicketInfo = ticket_info.sort((a, b) =>
      a.ticket_type < b.ticket_type ? -1 : 1
    );

    sortedTicketInfo.forEach(ticket => {
      if (ticket.ticket_type === TicketTypeVo.BuyCashSingle) {
        priceList.push(
          t('contenthome_single_own_price', {
            values: {
              value: ticket.price.toLocaleString(),
            },
          })
        );
      } else if (ticket.ticket_type === TicketTypeVo.BuyCashDiscountSingle) {
        priceList.push(
          t('contenthome_single_own_price', {
            values: {value: ticket.discount_price?.toLocaleString()},
          })
        );
      } else if (ticket.ticket_type === TicketTypeVo.RentCashSingle) {
        priceList.push(
          t('contenthome_single_rent_price', {
            values: {value: ticket.price.toLocaleString()},
          })
        );
      } else if (ticket.ticket_type === TicketTypeVo.RentCashDiscountSingle) {
        priceList.push(
          t('contenthome_single_rent_price', {
            values: {value: ticket.discount_price?.toLocaleString()},
          })
        );
      }
    });
  }

  const eventLog: EventLog = {
    eventMeta: {
      id: String(product_id),
      type: purchaseInfo.canRead || is_free ? 'productId' : undefined,
      name: title,
      category: itemSeriesDto.category,
      subcategory: itemSeriesDto.sub_category,
      provider: '서비스',
    },
  };

  return {
    id: uuidv4(),
    isChecked: false,
    isCheckMode: false,
    showPlayerIcon:
      slide_type === SlideTypeVo.TalkHumor ||
      slide_type === SlideTypeVo.VideoOnly ||
      slide_type === SlideTypeVo.VOD,
    type: ItemType.SingleListView,
    thumbnail: thumbnail.startsWith('http')
      ? thumbnail
      : getKageUrl({
          kageUrlPrefix: getEnv().kageUrlPrefix,
          kageId: thumbnail,
          resolution: 'th3',
        }),
    row1: title,
    row2: [
      getSingleUpdateDateText({
        bm,
        start_sale_dt,
        free_change_dt,
        singleIsFree: is_free,
        singleIsHidden: !!hidden,
      }),
      ...(slide_type === SlideTypeVo.Comic &&
      ![Bm.FreePreview, Bm.MixedWaitfree, Bm.MixedPay].includes(bm)
        ? [t('contenthome_ep_page', {values: {value: page_count}})]
        : []),
    ],
    row3: {
      badgeList,
      text:
        !ticket_info && is_free ? '' : (purchaseInfo.purchaseInfoText ?? ''),
      priceList,
    },
    scheme: `kakaopage://open/viewer?product_id=${product_id}&series_id=${itemSeriesDto.series_id}`,
    single: createSingle(itemSingleDto),
    isViewed: !!last_view,
    eventLog,
    discountRate: operator_property?.cash_info?.discount_rate,
    discountRateText: getDiscountRateText({
      discountRate: operator_property?.cash_info?.discount_rate,
      setDiscountRate: operator_property?.cash_info?.set_discount_rate,
    }),
  };
}

export function getSingleUpdateDateText({
  bm,
  start_sale_dt,
  free_change_dt,
  singleIsFree,
  singleIsHidden,
}: {
  bm: Bm;
  start_sale_dt: string;
  free_change_dt: string;
  singleIsFree: boolean;
  singleIsHidden: boolean;
}): string {
  const freeChargeDate = parseDate(free_change_dt);
  const startSaleDate = parseDate(start_sale_dt);
  if (
    isPreviewBm(bm) &&
    /* 미리보기 구간 여부 */ singleIsHidden &&
    !singleIsFree
  ) {
    const diff = getDateDifference({
      dateLeft: freeChargeDate,
      dateRight: new Date(),
    });
    const totalMinutes =
      diff.days * ONE_DAY_MINUTES + diff.hours * 60 + diff.minutes;
    if (totalMinutes > ONE_DAY_MINUTES) {
      return t('viewer_end_next_ea_period', {
        values: {
          value: Math.ceil(totalMinutes / ONE_DAY_MINUTES),
        },
      });
    } else {
      return t('viewer_end_next_ea_period', {
        values: {
          value: 1,
        },
      });
    }
  } else {
    // freeChargeDate가 1980.1.1인 초기값으로 설정되어있는 경우가 있습니다. 이런경우 saleDate를 노출해줍니다.
    return format(
      freeChargeDate < startSaleDate ? startSaleDate : freeChargeDate,
      t('date_formatter_short_year_dot')
    );
  }
}

function getPurchaseInfo({
  purchase_info,
  currentDate,
}: {
  purchase_info?: PurchaseInfoDto;
  currentDate: Date;
}): {purchaseInfoText?: string; canRead: boolean} {
  if (
    !purchase_info ||
    purchase_info.purchase_type === PurchaseTypeVo.NotPurchased
  ) {
    return {purchaseInfoText: undefined, canRead: false};
  }
  if (
    purchase_info.purchase_type === PurchaseTypeVo.Buy ||
    purchase_info.purchase_type === PurchaseTypeVo.SetBuy
  ) {
    return {purchaseInfoText: undefined, canRead: true};
  }

  if (purchase_info?.rental_period_by_minute) {
    if (purchase_info.rental_period_by_minute <= 0) {
      return {
        purchaseInfoText: t('common_episode_rental_expired'),
        canRead: false,
      };
    } else {
      const currentTimestamp =
        Date.now() + (purchase_info?.rental_period_by_minute || 0) * 60000;
      const date = new Date(currentTimestamp);

      return {
        purchaseInfoText: getRemainText(date, currentDate, false),
        canRead: true,
      };
    }
  } else {
    const rentExpireDate = parseDate(purchase_info.rent_expire_dt);
    const {totalSeconds} = getDateDifference({
      dateLeft: rentExpireDate,
      dateRight: currentDate,
    });

    if (totalSeconds <= 0) {
      return {
        purchaseInfoText: t('common_episode_rental_expired'),
        canRead: false,
      };
    } else {
      return {
        purchaseInfoText: getRemainText(rentExpireDate, currentDate),
        canRead: true,
      };
    }
  }
}

const ONE_DAY_MINUTES = 24 * 60;
